/**
 * Échelle de CATÉGORIES de la suite SEmply — les pastilles, puces et jetons
 * qui disent « de quelle famille » plutôt que « dans quel état ».
 *
 * ── Pourquoi elle ne se confond ni avec le sémantique ni avec les séries ──
 *
 * Trois familles de couleur cohabitent dans la charte, et les mélanger est
 * l'erreur classique :
 *
 *   sémantique  (`--color-success`, `--color-danger`…)  dit un ÉTAT. Réservé :
 *               une catégorie ne doit jamais emprunter le rouge d'une erreur.
 *   séries      (`--chart-series-1..7`, src/charts.js)   peint une MARQUE dans
 *               un graphique : validé à 3:1, donc trop pâle pour du texte —
 *               deux teintes sur sept seulement atteignent 4,5:1 en petit
 *               corps. Ne pas s'en servir pour une pastille.
 *   catégories  (ici)                                    porte une IDENTITÉ en
 *               TEXTE : chaque emplacement est une encre lisible ET son fond
 *               pâle, les deux allant ensemble.
 *
 * Comme les séries, l'échelle est la MÊME sous les neuf palettes : la
 * séparation entre catégories est un calcul, pas un goût, et la refaire par
 * palette reviendrait à la rater huit fois de plus. Seul le mode clair/sombre
 * la fait basculer.
 *
 * ── Ce qui a été vérifié ─────────────────────────────────────────────────
 *
 * Contraste, sur les fonds réels de la charte — `--bg-color` (#FFFFFF clair,
 * #14171C sombre), `--bg-color-light` des palettes clientes ET du back-office
 * (#F4F7F6, #F8F6F9, #1C2027), et le fond pâle de son propre emplacement :
 * **chaque encre tient au moins 4,5:1 partout**, le cas le plus serré étant
 * l'encre sur son propre fond pâle (4,61 à 4,65:1).
 *
 * Séparation, validateur de la compétence « dataviz », paires adjacentes :
 * CVD ΔE 9,5 clair / 8,4 sombre (cible ≥ 8) ; vision normale 18,2 / 18,1
 * (plancher ≥ 15). L'ORDRE des teintes fait partie du résultat — il a été
 * retenu par énumération des 5040 permutations, sous les deux seuils et dans
 * les deux modes à la fois. Réordonner sans rejouer ce calcul casse la
 * séparation : plusieurs ordres tombent à ΔE 11 en vision normale.
 *
 * ── Comment on s'en sert ─────────────────────────────────────────────────
 *
 * Une catégorie prend `--tag-ink-N` en couleur de texte et `--tag-bg-N` en
 * fond, du même N. Croiser deux emplacements (l'encre de 3 sur le fond de 5)
 * n'est vérifié par rien et ne doit pas se faire.
 *
 * Et la couleur ne porte jamais l'information seule : une pastille de
 * catégorie contient le NOM de la catégorie. C'est ce qui rend l'échelle
 * utilisable au-delà de sept familles, et lisible sans elle.
 */

/** Nombre d'emplacements. Au-delà, regrouper — ne pas inventer une 8e teinte. */
export const TAG_COUNT = 7;

/** Teintes, dans l'ordre retenu par le calcul. Le nom sert à la lecture. */
export const TAG_HUES = ['bleu', 'orange', 'aqua', 'violet', 'jaune', 'magenta', 'vert'];

const ENCRES_CLAIR = ['#146cce', '#bf4402', '#007e55', '#685dcf', '#956300', '#bb3c73', '#11800f'];
const FONDS_CLAIR = ['#eaf3ff', '#ffeee8', '#e2f9ec', '#f0f1ff', '#fff0dd', '#ffedf2', '#e7f8e6'];
const ENCRES_SOMBRE = ['#498fe7', '#dc6d43', '#00a370', '#8681e7', '#be8000', '#d86693', '#4ca047'];
const FONDS_SOMBRE = ['#15263c', '#391d12', '#0a2c1e', '#22223c', '#332106', '#381b25', '#172b15'];

function enJetons(encres, fonds) {
  const out = {};
  for (let i = 0; i < TAG_COUNT; i += 1) {
    out[`--tag-ink-${i + 1}`] = encres[i];
    out[`--tag-bg-${i + 1}`] = fonds[i];
  }
  return out;
}

export const TAGS_LIGHT = enJetons(ENCRES_CLAIR, FONDS_CLAIR);
export const TAGS_DARK = enJetons(ENCRES_SOMBRE, FONDS_SOMBRE);

/** Les jetons à poser sur une palette, selon qu'elle est sombre ou non. */
export function tagVarsFor(palette) {
  return palette?.isDark ? TAGS_DARK : TAGS_LIGHT;
}
